<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import clubApplicationService from '../services/clubApplication.service'
import ClubApplicationAcceptModal from '../components/ClubApplicationAcceptModal.vue'
import ClubApplicationRejectModal from '../components/ClubApplicationRejectModal.vue'
import ClubSignupLinkModal from '../components/ClubSignupLinkModal.vue'
import type { ClubApplicationListItem } from '../types/clubApplication.types'

const router = useRouter()
const authStore = useAuthStore()
const { t } = useI18n()

const clubUuid = computed(() => authStore.initData?.club?.uuid || '')

const hasPermission = (permission: string) => {
  const permissions = authStore.initData?.permission_model || []
  return permissions.includes(permission)
}

// COACH has view-only access
const canManage = computed(() => hasPermission('CLUB_APPLICATION_MANAGE'))

// State
const applications = ref<ClubApplicationListItem[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// Pagination
const currentPage = ref(1)
const perPage = ref(25)
const total = ref(0)
const lastPage = ref(1)

// Search
const searchQuery = ref('')

// Selection
const selectedUuids = ref<string[]>([])
const showBulkActionsDropdown = ref(false)
const openDropdownId = ref<string | null>(null)

// Modals
const showAcceptModal = ref(false)
const showRejectModal = ref(false)
const showLinkModal = ref(false)
const modalUuids = ref<string[]>([])
const modalName = ref<string | undefined>(undefined)

const fetchApplications = async () => {
  if (!clubUuid.value) return
  loading.value = true
  error.value = null
  try {
    const response = await clubApplicationService.getApplications(
      clubUuid.value,
      currentPage.value,
      perPage.value,
      searchQuery.value
    )
    applications.value = response.data?.applications || []
    total.value = response.total || 0
    lastPage.value = response.last_page || 1
    // Drop selections that are no longer on the list
    const visible = new Set(applications.value.map(a => a.uuid))
    selectedUuids.value = selectedUuids.value.filter(uuid => visible.has(uuid))
  } catch (err: any) {
    console.error('Fetch applications error:', err)
    error.value = err.response?.data?.message || t('clubApplications.errorFetching')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  currentPage.value = 1
  fetchApplications()
}

// Selection
const allSelected = computed(() =>
  applications.value.length > 0 && applications.value.every(a => selectedUuids.value.includes(a.uuid))
)

const toggleSelectAll = () => {
  selectedUuids.value = allSelected.value ? [] : applications.value.map(a => a.uuid)
}

const toggleSelection = (uuid: string) => {
  const idx = selectedUuids.value.indexOf(uuid)
  if (idx === -1) selectedUuids.value.push(uuid)
  else selectedUuids.value.splice(idx, 1)
}

const showBulkActions = computed(() => canManage.value && selectedUuids.value.length > 0)

// Dropdowns
const toggleDropdown = (event: Event, uuid: string) => {
  event.stopPropagation()
  openDropdownId.value = openDropdownId.value === uuid ? null : uuid
}

const closeAllDropdowns = () => {
  openDropdownId.value = null
  showBulkActionsDropdown.value = false
}

// Modal openers
const notActivatedCount = computed(() =>
  applications.value.filter(a => modalUuids.value.includes(a.uuid) && !a.is_activated).length
)

const openAccept = (uuids: string[]) => {
  closeAllDropdowns()
  modalUuids.value = uuids
  showAcceptModal.value = true
}

const openReject = (uuids: string[], name?: string) => {
  closeAllDropdowns()
  modalUuids.value = uuids
  modalName.value = name
  showRejectModal.value = true
}

const onDecisionDone = () => {
  showAcceptModal.value = false
  showRejectModal.value = false
  selectedUuids.value = selectedUuids.value.filter(uuid => !modalUuids.value.includes(uuid))
  modalUuids.value = []
  modalName.value = undefined
  // Last item on a page disappeared — step back a page
  if (applications.value.length <= 1 && currentPage.value > 1) currentPage.value--
  fetchApplications()
}

const goToDetail = (uuid: string) => {
  router.push(`/club-applications/${uuid}`)
}

// Pagination handlers
const changePage = (page: number) => {
  currentPage.value = page
  selectedUuids.value = []
  fetchApplications()
}

const changePerPage = () => {
  currentPage.value = 1
  selectedUuids.value = []
  fetchApplications()
}

const fromItem = computed(() => (total.value === 0 ? 0 : (currentPage.value - 1) * perPage.value + 1))
const toItem = computed(() => Math.min(currentPage.value * perPage.value, total.value))

const visiblePages = computed(() => {
  const maxVisible = 5
  const pages: number[] = []
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  const end = Math.min(lastPage.value, start + maxVisible - 1)
  if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1)
  for (let i = start; i <= end; i++) pages.push(i)
  return pages
})

// Format ISO date to YYYY-MM-DD HH:MM
const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}`
}

onMounted(() => {
  fetchApplications()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen" @click="closeAllDropdowns">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Back link -->
      <div class="mb-4">
        <router-link to="/dashboard" class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('clubApplications.backToDashboard') }}
        </router-link>
      </div>

      <!-- Page Header -->
      <div class="mb-4 md:mb-4 mb-2 flex justify-between items-center gap-2">
        <h1 class="text-2xl font-bold text-gray-900">
          {{ t('clubApplications.title') }} <span class="text-sm font-normal text-gray-600">({{ t('dashboard.found') }} {{ total }})</span>
        </h1>
        <div class="flex flex-col items-end gap-1">
          <button
            @click.stop="showLinkModal = true"
            class="px-4 py-2 md:px-4 md:py-2 px-2 py-1 bg-green-600 text-sm md:text-sm text-xs font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 flex items-center gap-2 md:gap-2 gap-1"
          >
            <svg class="w-4 h-4 md:w-4 md:h-4 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clip-rule="evenodd"/>
            </svg>
            {{ t('clubApplications.signupLink') }}
          </button>
          <router-link
            v-if="canManage"
            to="/club-applications/invitations"
            class="text-xs md:text-sm text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
          >
            {{ t('clubInvitations.sendLinkCta') }}
          </router-link>
        </div>
      </div>

      <!-- Search -->
      <div class="mb-4 flex justify-end">
        <div class="flex">
          <input
            v-model="searchQuery"
            @keyup.enter="handleSearch"
            type="text"
            :placeholder="t('dashboard.search')"
            class="w-48 px-2.5 py-1.5 text-sm border-2 border-r-0 border-gray-300 rounded-l focus:outline-none focus:border-blue-600"
          >
          <button
            @click="handleSearch"
            class="px-2.5 py-1.5 bg-blue-600 text-white rounded-r hover:bg-blue-700 border-2 border-blue-600"
          >
            <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Bulk Actions Bar -->
      <div
        v-if="showBulkActions"
        class="mb-4 flex flex-col gap-3 bg-blue-50 border-2 border-blue-300 rounded p-3"
      >
        <div class="flex items-center gap-3">
          <div class="relative">
            <button
              @click.stop="showBulkActionsDropdown = !showBulkActionsDropdown"
              class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 flex items-center gap-2"
            >
              <span>{{ t('dashboard.optionsForSelected') }} ({{ selectedUuids.length }})</span>
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div
              v-show="showBulkActionsDropdown"
              class="absolute left-0 mt-2 w-56 bg-white border-2 border-gray-300 rounded shadow-lg z-50"
              @click.stop
            >
              <button
                @click="openAccept([...selectedUuids])"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
                {{ t('clubApplications.accept') }}
              </button>
              <button
                @click="openReject([...selectedUuids])"
                class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                </svg>
                {{ t('clubApplications.reject') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mb-4 bg-red-50 border-2 border-red-300 rounded p-3">
        <p class="text-sm text-red-800">{{ error }}</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-sm text-gray-600">{{ t('dashboard.loading') }}</p>
      </div>

      <!-- Desktop Table -->
      <div v-if="!loading && applications.length > 0" class="hidden md:block bg-white border-2 border-gray-300 rounded shadow-sm overflow-y-visible" style="overflow-x: clip;">
        <table class="w-full text-sm">
          <thead class="bg-gray-100 border-b-2 border-gray-300">
            <tr>
              <th v-if="canManage" class="px-3 py-2 text-left">
                <input
                  type="checkbox"
                  :checked="allSelected"
                  @change="toggleSelectAll"
                  class="w-4 h-4 text-blue-600 border-gray-300 rounded"
                >
              </th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700">{{ t('players.firstName') }}</th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700">{{ t('players.lastName') }}</th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700">{{ t('players.email') }}</th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700">{{ t('clubApplications.registrationDate') }}</th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700">{{ t('clubApplications.activation') }}</th>
              <th class="px-3 py-2 text-right text-xs font-semibold text-gray-700">{{ t('dashboard.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="application in applications"
              :key="application.uuid"
              class="border-b border-gray-200 hover:bg-gray-50"
            >
              <td v-if="canManage" class="px-3 py-2">
                <input
                  type="checkbox"
                  :checked="selectedUuids.includes(application.uuid)"
                  @change="toggleSelection(application.uuid)"
                  class="w-4 h-4 text-blue-600 border-gray-300 rounded"
                >
              </td>
              <td class="px-3 py-2">
                <a
                  :href="`/club-applications/${application.uuid}`"
                  @click.prevent="goToDetail(application.uuid)"
                  class="text-blue-600 hover:text-blue-800 font-medium"
                >
                  {{ application.first_name }}
                </a>
              </td>
              <td class="px-3 py-2">
                <a
                  :href="`/club-applications/${application.uuid}`"
                  @click.prevent="goToDetail(application.uuid)"
                  class="text-blue-600 hover:text-blue-800 font-medium"
                >
                  {{ application.last_name }}
                </a>
                <span
                  v-if="application.possible_duplicate"
                  class="ml-2 inline-block px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-medium rounded"
                >{{ t('clubApplications.possibleDuplicate') }}</span>
              </td>
              <td class="px-3 py-2 text-gray-900">{{ application.email }}</td>
              <td class="px-3 py-2 text-gray-900">{{ formatDateTime(application.created_at) }}</td>
              <td class="px-3 py-2">
                <span
                  v-if="application.is_activated"
                  class="inline-block px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded"
                >{{ t('clubApplications.activated') }}</span>
                <span
                  v-else
                  class="inline-block px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded"
                >{{ t('clubApplications.notActivated') }}</span>
              </td>
              <td class="px-2 py-2 sticky right-0 bg-inherit" style="position: relative;">
                <div class="flex items-center gap-1 justify-end">
                  <button
                    @click="toggleDropdown($event, application.uuid)"
                    class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                  >
                    {{ t('dashboard.actions') }}
                  </button>
                  <div :class="['custom-dropdown', 'dropdown-bottom', { 'show': openDropdownId === application.uuid }]">
                    <router-link :to="`/club-applications/${application.uuid}`">{{ t('dashboard.view') }}</router-link>
                    <button v-if="canManage" @click.stop="openAccept([application.uuid])">{{ t('clubApplications.accept') }}</button>
                    <button v-if="canManage" class="delete-action" @click.stop="openReject([application.uuid], `${application.first_name} ${application.last_name}`)">{{ t('clubApplications.reject') }}</button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Cards -->
      <div v-if="!loading && applications.length > 0" class="md:hidden space-y-2">
        <div
          v-for="application in applications"
          :key="application.uuid"
          class="bg-white border-2 border-gray-300 rounded shadow-sm"
        >
          <div class="flex items-start gap-2 p-3">
            <input
              v-if="canManage"
              type="checkbox"
              :checked="selectedUuids.includes(application.uuid)"
              @change.stop="toggleSelection(application.uuid)"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded flex-shrink-0 mt-1"
            >
            <div class="flex-1 min-w-0">
              <a
                :href="`/club-applications/${application.uuid}`"
                @click.prevent="goToDetail(application.uuid)"
                class="text-sm text-blue-600 hover:underline font-medium"
              >
                {{ application.first_name }} {{ application.last_name }}
              </a>
              <div class="text-xs text-gray-600 mt-0.5 break-all">{{ application.email }}</div>
              <div class="text-xs text-gray-600 mt-0.5">{{ t('clubApplications.registrationDate') }}: {{ formatDateTime(application.created_at) }}</div>
              <div class="flex flex-wrap gap-1 mt-1">
                <span
                  v-if="application.is_activated"
                  class="inline-block px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-medium rounded"
                >{{ t('clubApplications.activated') }}</span>
                <span
                  v-else
                  class="inline-block px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-medium rounded"
                >{{ t('clubApplications.notActivated') }}</span>
                <span
                  v-if="application.possible_duplicate"
                  class="inline-block px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-medium rounded"
                >{{ t('clubApplications.possibleDuplicate') }}</span>
              </div>
            </div>
            <div style="position: relative;">
              <button
                @click.stop="toggleDropdown($event, application.uuid)"
                class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
              >
                {{ t('dashboard.actions') }}
              </button>
              <div :class="['custom-dropdown', 'dropdown-bottom', { 'show': openDropdownId === application.uuid }]">
                <router-link :to="`/club-applications/${application.uuid}`">{{ t('dashboard.view') }}</router-link>
                <button v-if="canManage" @click.stop="openAccept([application.uuid])">{{ t('clubApplications.accept') }}</button>
                <button v-if="canManage" class="delete-action" @click.stop="openReject([application.uuid], `${application.first_name} ${application.last_name}`)">{{ t('clubApplications.reject') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && !error && applications.length === 0" class="text-center py-12 bg-white border-2 border-gray-300 rounded">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">{{ t('clubApplications.empty') }}</h3>
        <p class="mt-1 text-sm text-gray-500">{{ t('clubApplications.emptyHint') }}</p>
        <div class="mt-6">
          <button
            @click.stop="showLinkModal = true"
            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
          >
            {{ t('clubApplications.signupLink') }}
          </button>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && applications.length > 0" class="mt-4 flex flex-col lg:flex-row justify-between items-center gap-4 bg-white border-2 border-gray-300 rounded p-4">
        <div class="text-sm text-gray-700 w-full lg:w-auto text-center lg:text-left">
          {{ t('dashboard.displaying') }} {{ fromItem }} {{ t('dashboard.to') }} {{ toItem }} {{ t('dashboard.of') }} {{ total }}
        </div>
        <div class="flex flex-col md:flex-row items-center gap-4 w-full lg:w-auto">
          <div class="flex items-center gap-2 w-full md:w-auto justify-center md:justify-start">
            <label for="perPage" class="text-sm text-gray-700">{{ t('clubApplications.perPage') }}</label>
            <select id="perPage" v-model.number="perPage" @change="changePerPage" class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </div>
          <div class="flex items-center gap-1 w-full md:w-auto justify-center md:justify-start">
            <button
              @click="changePage(currentPage - 1)"
              :disabled="currentPage === 1"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &laquo;
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="changePage(page)"
              :class="[
                'px-3 py-1 border-2 rounded text-sm',
                page === currentPage
                  ? 'border-gray-900 bg-gray-900 text-white font-medium'
                  : 'border-gray-300 bg-white hover:bg-gray-50'
              ]"
            >
              {{ page }}
            </button>
            <button
              @click="changePage(currentPage + 1)"
              :disabled="currentPage === lastPage"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>

  <ClubApplicationAcceptModal
    :show="showAcceptModal"
    :club-uuid="clubUuid"
    :application-uuids="modalUuids"
    :not-activated-count="notActivatedCount"
    @close="showAcceptModal = false"
    @done="onDecisionDone"
  />

  <ClubApplicationRejectModal
    :show="showRejectModal"
    :club-uuid="clubUuid"
    :application-uuids="modalUuids"
    :name="modalName"
    @close="showRejectModal = false"
    @done="onDecisionDone"
  />

  <ClubSignupLinkModal
    :show="showLinkModal"
    :club-uuid="clubUuid"
    @close="showLinkModal = false"
  />
</template>
